/**
 * `external-sharing` Phase 6 Session 6.2 — `useVersionDiff({ from, to })`.
 *
 * TanStack Query wrapper over `GET /api/shares/diff?from=<kind>:<uuid>&to=<kind>:<uuid>`
 * (Phase 2 Session 2.6). The server's `diffQuerySchema` parses both sides
 * via the `<kind>:<uuid>` envelope where `kind ∈ { conversation, frozen_state }`
 * (see `packages/shared-types/src/lib/sharing.ts:139-167`).
 *
 * Four pairings the server handles end-to-end:
 *   - conversation ↔ conversation  (graph edge clicks; auth-side fork lineage)
 *   - conversation ↔ frozen_state  (live conv vs. snapshot-at-share-time)
 *   - frozen_state ↔ conversation  (snapshot vs. live conv)
 *   - frozen_state ↔ frozen_state  (two snapshots — useful for re-shares)
 *
 * Phase 6 Session 6.1's `<VersionGraphEdge>` callback emits the first
 * pairing exclusively (graph nodes ARE conversations); the other 3
 * pairings are reserved for future surfaces.
 *
 * Cache key: `[shares, 'diff', fromSerialized, toSerialized]`. Different
 * `from`/`to` swaps produce different cache entries — server response
 * shape is symmetric in field semantics but inverted (oldValue ↔ newValue),
 * so caching them as distinct entries is correct.
 */
import { useQuery } from '@tanstack/react-query';
import { ApiError } from '@/lib/api';
import { getDiff } from '@/lib/shares-api';
import { queryKeys } from '@/lib/query-keys';
import type { DiffSide, VersionDiffResponse } from '@arcanum-hq/shared-types';

function serializeSide(side: DiffSide): string {
  return `${side.kind}:${side.id}`;
}

export interface UseVersionDiffArgs {
  from: DiffSide | null;
  to: DiffSide | null;
}

export function useVersionDiff({ from, to }: UseVersionDiffArgs) {
  const fromSerialized = from ? serializeSide(from) : '';
  const toSerialized = to ? serializeSide(to) : '';
  return useQuery<VersionDiffResponse>({
    queryKey: queryKeys.shares.diff(fromSerialized, toSerialized),
    queryFn: () => {
      if (!from || !to) throw new Error('from + to required');
      return getDiff(serializeSide(from), serializeSide(to));
    },
    enabled: !!(from && to),
    staleTime: 60_000,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 404) return false;
      return failureCount < 2;
    },
  });
}
